import React, { useState, useEffect, useContext } from 'react'
import { HeaderContext } from '../App'
import { connect } from 'react-redux'
import Checkbox from './Checkbox'
import Radio from './Radio'
import recaptcha from '~/helpers/loadRecaptcha'
import http, { preparePayload, formatErrors, wrapButtonToggle } from '~/http'
import states from '~/constants/states'
import ErrorMessage from '~/components/ErrorMessage'
import ErrorList from '~/components/ErrorList'
import Button from '~/components/Button'
import Select from '~/components/Select'
import TextField from './TextField'
import DatePicker from '~/components/DatePicker'
import Banner from '~/components/Banner'
import { formatPhoneNumber } from '~/helpers/phoneNumber'
import translateFromEnglish from '~/helpers/translateFromEnglish'
import useScreenResize from '~/custom-hooks/useScreenResize'

// --vars

const genders = {
  Female: 'Female',
  Male: 'Male',
  Other: 'Other'
}

const gendersSpanish = {
  Female: 'Mujer',
  Male: 'Hombre',
  Other: 'Otro'
}

const phoneTypes = {
  Mobile: 'Mobile',
  Work: 'Work',
  Residential: 'Residential'
}

const phoneTypesSpanish = {
  Mobile: 'Móvil',
  Work: 'Trabajo',
  Residential: 'Residencial'
}

const raceList = [
  {
    english: 'American Indian/Alaskan Native',
    spanish: 'Indio Americano/Nativo de Alaska'
  },
  {
    english: 'Asian',
    spanish: 'Asiático'
  },
  {
    english: 'Black or African American',
    spanish: 'Negro o Afroamericano'
  },
  {
    english: 'Hawaiian/Pacific Islander',
    spanish: 'Hawaiano/isleño del Pacífico'
  },
  {
    english: 'White',
    spanish: 'Blanco'
  },
  {
    english: 'Middle Eastern or North African',
    spanish: 'Oriente Medio o África del Norte'
  },
  {
    english: 'Unknown',
    spanish: 'Desconocido'
  }
]

// Array of json objects with an ID key and a language key
const makeOptions = obj => Object.keys(obj).reduce((acc, x) => {
  acc.push({ value: x, label: obj[x] })
  return acc
}, [])

const stateOptions = makeOptions(states)

const genderOptions = makeOptions(genders)
const genderOptionsSpanish = makeOptions(gendersSpanish)
const phoneTypeOptions = makeOptions(phoneTypes)
const phoneTypeOptionsSpanish = makeOptions(phoneTypesSpanish)

const defaultCompanySponsorshipInfo = {
  companyGaveTimeOff: false,
  companyIsPaying: false,
  companyWillReimburse: false,
  selfEmployed: false,
  sponsoringCompanyId: undefined,
  sponsoringCompanyName: '',
  sponsoringCompanyAddress: '',
  sponsoringCompanyCity: '',
  sponsoringCompanyState: '',
  sponsoringCompanyZipCode: '',
  companyName: ' ',
  companyAddress: ' ',
  companyCity: ' ',
  companyState: '',
  companyZipCode: ' ',
  supervisorName: '',
  supervisorEmail: '',
  supervisorPhoneNumber: '',
  employedByDifferentCompany: false
}

// --components
function AddStudentPage (props) {
  const [globalError, setGlobalError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false)

  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [lastName, setLastName] = useState('')
  const [gender, setGender] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [hasAttendedBefore, setHasAttendedBefore] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneType, setPhoneType] = useState('')
  const [email, setEmail] = useState('')
  const [isWedIndustryStudent, setIsWedIndustryStudent] = useState(false)
  const [educationLevel, setEducationLevel] = useState('')
  const [educationLevels, setEducationLevels] = useState([])
  const [races, setRaces] = useState([])
  const [isHispanicLatino, setIsHispanicLatino] = useState(false)
  const [language, setLanguage] = useState(undefined)
  const [languageOptions, setLanguageOptions] = useState([])
  const [translationLanguage, setTranslationLanguage] = useState(false)
  const [sectionNumber, setSectionNumber] = useState('')
  const [companySponsorshipInfo, setCompanySponsorshipInfo] = useState(defaultCompanySponsorshipInfo)
  const [sectionOptions, setSectionOptions] = useState([])
  const [sponsoringCompanies, setSponsoringCompanies] = useState([])
  const [clearChildSelect, setClearChildSelect] = useState(false)

  const screenResize = useScreenResize()
  const screenSize = screenResize.size

  const getStudent = () => ({
    firstName,
    middleName,
    lastName,
    gender,
    birthDate,
    address1,
    address2,
    city,
    state,
    zipCode,
    phoneNumber,
    phoneType,
    email,
    hasAttendedBefore,
    language,
    educationLevel,
    races: extractEnglishRaceNames(),
    isHispanicLatino,
    sectionNumber,
    isWedIndustryStudent
  })

  const extractEnglishRaceNames = () => {
    return races.map(x => x.english)
  }

  const getPersonOptionData = async (isWedIndustryParam) => {
    const response = await http(setGlobalError, http => http.get('/api/SelfService/Students/GetStudentOptionData'))
    const educationLevelOptions = response.data.educationLevels.map(x => {
      return { label: x.name, value: x.id }
    })
    const languages = response.data.languages.map(x => {
      return { label: x.name, value: x.id }
    })

    if (isWedIndustryParam) {
      // Wed Industry Students will see a different order to the education level list.
      // Remove all education levels below high school and add them to the end.
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'Middle School, 6th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'Middle School, 7th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'Middle School, 8th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'High School, 9th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'High School, 10th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'High School, 11th Grade'), 1)[0])
      educationLevelOptions.push(educationLevelOptions.splice(educationLevelOptions
        .findIndex(x => x.label === 'High School, 12th Grade'), 1)[0])

      // Switch Less than a high school diploma and high school graduate.
      const lessThanHighSchoolIndex = educationLevelOptions.findIndex(x => x.label === 'Less than a high school degree')
      const lessThanHighSchoolElem = educationLevelOptions[lessThanHighSchoolIndex]
      educationLevelOptions.splice(lessThanHighSchoolIndex, 1)

      const highSchoolGraduateIndex = educationLevelOptions.findIndex(x => x.label === 'High school graduate')
      const highSchoolGraduateElem = educationLevelOptions[highSchoolGraduateIndex]
      educationLevelOptions.splice(highSchoolGraduateIndex, 1)

      educationLevelOptions.unshift(highSchoolGraduateElem, lessThanHighSchoolElem)
    }

    setEducationLevels(educationLevelOptions)
    setLanguageOptions(languages)
  }

  const getSponsoringCompanies = async (value) => {
    const response = await http(setGlobalError, http => http.get(`/api/SelfService/Students/${value}/GetSponsoringCompanies`))

    if (response.data.length === 1) {
      setCompanySponsorshipInfo({
        ...companySponsorshipInfo,
        sponsoringCompanyId: response.data[0].id,
        sponsoringCompanyName: response.data[0].name,
        sponsoringCompanyAddress: response.data[0].address,
        sponsoringCompanyCity: response.data[0].city,
        sponsoringCompanyState: response.data[0].state,
        sponsoringCompanyZipCode: response.data[0].zipCode
      })
    }

    setSponsoringCompanies(response.data)
  }

  const updateSectionNumber = (value) => {
    setSectionNumber(value)
    getSponsoringCompanies(value)
    setCompanySponsorshipInfo(defaultCompanySponsorshipInfo)
    setClearChildSelect(true)
  }

  const searchSections = async () => {
    const sectionSearchInfo = {
      sectionDate: new Date(Date.now())
    }
    const response = await http(setGlobalError, http => http.post('/api/SelfService/Students/WedIndustrySectionSearch', sectionSearchInfo))
    const sectionSearchData = response.data
    const dropdownData = sectionSearchData.map(x => {
      return { value: x.courseSectionNumber, label: `${x.courseSectionNumber} - ${x.courseTitle}` }
    })

    setSectionOptions(dropdownData)
  }

  const { setHeaderInfo } = useContext(HeaderContext)
  const isCartLink = window.location.hash.includes('cart=true')

  useEffect(() => {
    // TODO: Get rid of industry student parameter
    setIsWedIndustryStudent(true)
    getPersonOptionData(true)
  }, [])

  useEffect(() => {
    searchSections()
  }, [])

  useEffect(() => {
    setHeaderInfo({ pageTitle: 'Add Student' })
  }, [])

  return (
    <div className='page add-student'>
      <Banner message='Enroll' />
      <div className='page-body'>
        {globalError && <ErrorMessage message={globalError} />}
        {Object.keys(fieldErrors).length > 0 && <ErrorList errors={fieldErrors} />}

        {/*
          WED INDUSTRY SECTION - This section is only visible if the user scans a QR code with the isWedIndustry=true parameter or puts it in the URL
        */}

        {/* Language and Class Section Number */}
        <>
          <div style={{ marginBottom: '1rem' }}>
            <h4 className='row'>{!translationLanguage ? '¿Necesitas esta página en español?' : 'Do you need this page in English?'}</h4>
            <div className='row'>
              <Radio
                label='English'
                value='English'
                name='languageSelection'
                checked={translationLanguage === false}
                onChange={(evt) => {
                  setTranslationLanguage(false)
                }}
              />
              <Radio
                label='Español'
                value='Español'
                name='languageSelection'
                checked={translationLanguage === true}
                onChange={(evt) => {
                  setTranslationLanguage(true)
                }}
              />
            </div>
          </div>
          <div className='row'>
            <Select
              className='width-100-percent'
              label={translateFromEnglish('Class Section Number *', 'Número de sección de clase *', translationLanguage)}
              required
              fieldErrors={fieldErrors}
              options={sectionOptions}
              onChange={evt => updateSectionNumber(evt.value)}
            />
          </div>
        </>

        {/* Email */}
        <div className='row'>
          <TextField
            className='cell'
            label={translateFromEnglish('Email *', 'Correo Electrónico *', translationLanguage)}
            required
            invalid={fieldErrors.Email != null}
            value={email}
            onChange={evt => setEmail(evt.target.value)}
          />
        </div>

        {/* First and Middle Name */}
        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('First Name *', 'Nombre *', translationLanguage)}
                required
                invalid={fieldErrors['First Name'] != null}
                value={firstName}
                onChange={evt => setFirstName(evt.target.value)}
                fullwidth
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Middle Name', 'Segundo Nombre', translationLanguage)}
                invalid={fieldErrors['Middle Name'] != null}
                value={middleName}
                onChange={evt => setMiddleName(evt.target.value)}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label={translateFromEnglish('First Name *', 'Nombre *', translationLanguage)}
              required
              invalid={fieldErrors['First Name'] != null}
              value={firstName}
              onChange={evt => setFirstName(evt.target.value)}
              fullwidth
            />
            <TextField
              className='width-75-percent'
              label={translateFromEnglish('Middle Name', 'Segundo Nombre', translationLanguage)}
              invalid={fieldErrors['Middle Name'] != null}
              value={middleName}
              onChange={evt => setMiddleName(evt.target.value)}
            />
          </div>
        )}

        {/* Last Name and Gender */}
        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Last Name *', 'Apellido *', translationLanguage)}
                required
                invalid={fieldErrors['Last Name'] != null}
                value={lastName}
                onChange={evt => setLastName(evt.target.value)}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label={translateFromEnglish('Gender', 'Género', translationLanguage)}
                fieldErrors={fieldErrors}
                options={translationLanguage ? genderOptionsSpanish : genderOptions}
                setter={setGender}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label={translateFromEnglish('Last Name *', 'Apellido *', translationLanguage)}
              required
              invalid={fieldErrors['Last Name'] != null}
              value={lastName}
              onChange={evt => setLastName(evt.target.value)}
              fullwidth
            />
            <Select
              className='width-100-percent'
              label={translateFromEnglish('Gender', 'Género', translationLanguage)}
              required
              fieldErrors={fieldErrors}
              options={translationLanguage ? genderOptionsSpanish : genderOptions}
              setter={setGender}
            />
          </div>
        )}

        {/* Phone */}
        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Phone', 'Teléfono', translationLanguage)}
                invalid={fieldErrors['Phone Number'] != null}
                value={phoneNumber}
                onChange={evt => setPhoneNumber(evt.target.value)}
                onBlur={(evt) => setPhoneNumber(formatPhoneNumber(evt.target.value))}
                fullwidth
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label={translateFromEnglish(`Phone Type${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`, `Tipo de Teléfono${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`, translationLanguage)}
                required={phoneNumber !== ''}
                fieldErrors={fieldErrors}
                options={translationLanguage ? phoneTypeOptionsSpanish : phoneTypeOptions}
                setter={setPhoneType}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-75'>
            <TextField
              label={translateFromEnglish('Phone', 'Teléfono', translationLanguage)}
              invalid={fieldErrors['Phone Number'] != null}
              value={phoneNumber}
              onChange={evt => setPhoneNumber(evt.target.value)}
              onBlur={(evt) => setPhoneNumber(formatPhoneNumber(evt.target.value))}
              fullwidth
            />
            <Select
              className='width-100-percent'
              label={translateFromEnglish(`Phone Type${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`, `Tipo de Teléfono${phoneNumber != null && phoneNumber !== '' ? ' *' : ''}`, translationLanguage)}
              required={phoneNumber !== ''}
              fieldErrors={fieldErrors}
              options={translationLanguage ? phoneTypeOptionsSpanish : phoneTypeOptions}
              setter={setPhoneType}
            />
          </div>
        )}

        {/* Birth Date and Education Level */}
        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <DatePicker
                className='cell'
                name='Birth Date'
                label={translateFromEnglish('DOB ', 'Fecha de Nacimiento ', translationLanguage)}
                fieldErrors={fieldErrors}
                value={birthDate}
                setter={setBirthDate}
              />
            </div>
            <div className='row'>
              <Select
                className='cell'
                label={translateFromEnglish('Education Level', 'Nivel de Educación', translationLanguage)}
                fieldErrors={fieldErrors}
                options={educationLevels}
                setter={setEducationLevel}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <>
            <div className='row anchor-width-50'>
              <DatePicker
                className='cell'
                name='Birth Date'
                label={translateFromEnglish('DOB ', 'Fecha de Nacimiento ', translationLanguage)}
                fieldErrors={fieldErrors}
                value={birthDate}
                setter={setBirthDate}
              />
              <Select
                className='width-50-percent'
                label={translateFromEnglish('Education Level', 'Nivel de Educación', translationLanguage)}
                fieldErrors={fieldErrors}
                options={educationLevels}
                setter={setEducationLevel}
              />
            </div>
          </>
        )}

        <div className='row'>
          <h5>{translateFromEnglish('Race (check any that apply)', 'Raza (marque las que correspondan)', translationLanguage)}</h5>
        </div>
        {
          raceList.map((raceObj, idx) =>
            <div className='row' key={idx}>
              <Checkbox
                id={`${raceObj}-${idx}`}
                label={translationLanguage ? raceObj.spanish : raceObj.english}
                checked={races.includes(raceObj)}
                onChange={(evt) => {
                  const isChecked = races.includes(raceObj)
                  const personRaces = [].concat(races)

                  if (isChecked) {
                    personRaces.splice(personRaces.indexOf(raceObj), 1)
                  } else {
                    personRaces.push(raceObj)
                  }

                  setRaces(personRaces.slice(0))
                }}
              />
            </div>
          )
        }

        <div className='row'>
          <h5>Hispanic / Latino</h5>
        </div>
        <div className='row'>
          <Checkbox
            id='latino'
            checked={isHispanicLatino}
            onChange={(evt) => setIsHispanicLatino(evt.target.checked)}
            label={translateFromEnglish('Yes', 'Sí', translationLanguage)}
          />
        </div>

        <div className='row'>
          <h5>{translateFromEnglish('Language', 'Idioma', translationLanguage)}</h5>
        </div>
        <div className='row anchor-width-100'>
          <Select
            className='width-100-percent'
            label={translateFromEnglish('Please Select Your Primary Language', 'Por favor, seleccione su idioma principal', translationLanguage)}
            fieldErrors={fieldErrors}
            options={languageOptions}
            setter={setLanguage}
          />
        </div>

        <div className='row'>
          <h5>{translateFromEnglish('Current address', 'Dirección actual', translationLanguage)}</h5>
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label={translateFromEnglish('Address 1', 'Dirección 1', translationLanguage)}
            invalid={fieldErrors.Address1 != null}
            value={address1}
            onChange={evt => setAddress1(evt.target.value)}
          />
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label={translateFromEnglish('Address 2', 'Dirección 2', translationLanguage)}
            invalid={fieldErrors.Address2 != null}
            value={address2}
            onChange={evt => setAddress2(evt.target.value)}
          />
        </div>

        <div className='row'>
          <TextField
            className='cell'
            label={translateFromEnglish('City', 'Ciudad', translationLanguage)}
            invalid={fieldErrors.City != null}
            value={city}
            onChange={evt => setCity(evt.target.value)}
          />
        </div>

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <Select
                className='cell'
                label={translateFromEnglish('State', 'Estado', translationLanguage)}
                invalid={fieldErrors.State != null}
                options={stateOptions}
                setter={setState}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Zip Code', 'Código Postal', translationLanguage)}
                invalid={fieldErrors['Zip Code'] != null}
                value={zipCode}
                onChange={evt => setZipCode(evt.target.value)}
              />
            </div>
          </>
        )}
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <div className='row anchor-width-100'>
            <Select
              className='width-100-percent'
              label={translateFromEnglish('State', 'Estado', translationLanguage)}
              invalid={fieldErrors.State != null}
              options={stateOptions}
              setter={setState}
            />
            <TextField
              className='width-75-percent'
              label={translateFromEnglish('Zip Code', 'Código Postal', translationLanguage)}
              invalid={fieldErrors['Zip Code'] != null}
              value={zipCode}
              onChange={evt => setZipCode(evt.target.value)}
            />
          </div>
        )}

        <div className='row'>
          <Checkbox
            checked={hasAttendedBefore}
            onChange={(evt) => setHasAttendedBefore(evt.target.checked)}
            label={translateFromEnglish('Please check the box if this student has attended Francis Tuttle classes before', 'Por favor, marque la casilla si este estudiante ha asistido a las clases de Francis Tuttle antes', translationLanguage)}
          />
        </div>
      </div>

      <CompanySponsorshipForm
        companySponsorshipInfo={companySponsorshipInfo}
        setCompanySponsorshipInfo={setCompanySponsorshipInfo}
        sponsoringCompanies={sponsoringCompanies}
        isWedIndustryStudent={isWedIndustryStudent}
        fieldErrors={fieldErrors}
        translationLanguage={translationLanguage}
        screenSize={screenSize}
        clearChildSelect={clearChildSelect}
        setClearChildSelect={setClearChildSelect}
      />

      <div className='page-body centered'>
        <Button
          unelevated
          disabled={saveButtonDisabled}
          label={translateFromEnglish('Save and Continue', 'Guardar y Continuar', translationLanguage)}
          onClick={
            wrapButtonToggle(setSaveButtonDisabled, () => saveStudent(props, getStudent(), companySponsorshipInfo, isWedIndustryStudent, setGlobalError, setFieldErrors, isCartLink))
          }
          theme={['primaryBg']}
        />
      </div>

    </div>
  )
}

function CompanySponsorshipForm ({ companySponsorshipInfo, setCompanySponsorshipInfo, sponsoringCompanies, isWedIndustryStudent, fieldErrors, translationLanguage, screenSize, clearChildSelect, setClearChildSelect }) {
  const companySupported =
    companySponsorshipInfo.companyGaveTimeOff ||
    companySponsorshipInfo.companyIsPaying ||
    companySponsorshipInfo.companyWillReimburse

  let sponsorshipComponent

  if (sponsoringCompanies.length > 1) {
    const sponsoringCompaniesOptions = sponsoringCompanies.map((company, idx) => ({
      value: company.id,
      label: company.name
    }))

    sponsorshipComponent = (
      <>
        <Select
          className='cell'
          label={translateFromEnglish('Company Name *', 'Nombre de la Compañía *', translationLanguage)}
          fieldErrors={fieldErrors}
          options={sponsoringCompaniesOptions}
          clearOnSelect={clearChildSelect}
          onChange={evt => {
            setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              sponsoringCompanyId: evt.value,
              sponsoringCompanyName: sponsoringCompanies.find(x => x.id === evt.value).name,
              sponsoringCompanyAddress: sponsoringCompanies.find(x => x.id === evt.value).address,
              sponsoringCompanyCity: sponsoringCompanies.find(x => x.id === evt.value).city,
              sponsoringCompanyState: sponsoringCompanies.find(x => x.id === evt.value).state,
              sponsoringCompanyZipCode: sponsoringCompanies.find(x => x.id === evt.value).zipCode
            })
            setClearChildSelect(false)
          }}
          required={companySupported}
        />
      </>
    )
  } else {
    sponsorshipComponent = (
      <>
        <div className='row'>
          <TextField
            className='cell'
            label={translateFromEnglish('Company Name *', 'Nombre de la Empresa *', translationLanguage)}
            value={companySponsorshipInfo.sponsoringCompanyName}
            invalid={fieldErrors['Company Name'] != null}
            disabled
            required={companySupported}
          />
        </div>
      </>
    )
  }

  const selfEmploymentComponent = (
    !companySponsorshipInfo.selfEmployed && (
      <>
        {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Email', 'Correo Electrónico del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorEmail}
                invalid={fieldErrors['Supervisor Email'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorEmail: evt.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Name', 'Nombre del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorName}
                invalid={fieldErrors['Supervisor Name'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorName: evt.target.value })}
              />
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Phone Number', 'Número de Teléfono del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorPhoneNumber}
                invalid={fieldErrors['Supervisor Phone Number'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: evt.target.value })}
                onBlur={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: formatPhoneNumber(evt.target.value) })}
              />
            </div>
          </>
        )}

        {(screenSize === 'xs' || screenSize === 'sm') && (
          <>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Email', 'Correo Electrónico del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorEmail}
                invalid={fieldErrors['Supervisor Email'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorEmail: evt.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Name', 'Nombre del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorName}
                invalid={fieldErrors['Supervisor Name'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorName: evt.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                className='cell'
                label={translateFromEnglish('Supervisor\'s Phone Number', 'Número de Teléfono del Supervisor', translationLanguage)}
                value={companySponsorshipInfo.supervisorPhoneNumber}
                invalid={fieldErrors['Supervisor Phone Number'] != null}
                onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: evt.target.value })}
                onBlur={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, supervisorPhoneNumber: formatPhoneNumber(evt.target.value) })}
              />
            </div>
          </>
        )}
      </>
    )
  )

  const companyComponent = (
    <>
      <div className='row'>
        <TextField
          className='cell'
          label={translateFromEnglish('Company Name *', 'Nombre de la Compañía *', translationLanguage)}
          value={companySponsorshipInfo.companyName}
          invalid={fieldErrors['Company Name'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyName: evt.target.value })}
          required={companySupported}
        />
      </div>

      <div className='row'>
        <TextField
          className='cell'
          label={translateFromEnglish('Company Address', 'Dirección de la Compañía', translationLanguage)}
          value={companySponsorshipInfo.companyAddress}
          invalid={fieldErrors['Company Address'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyAddress: evt.target.value })}
        />
      </div>

      <div className='row'>
        <TextField
          className='cell'
          label={translateFromEnglish('City', 'Ciudad', translationLanguage)}
          value={companySponsorshipInfo.companyCity}
          invalid={fieldErrors['Company City'] != null}
          onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyCity: evt.target.value })}
        />
      </div>
      {(screenSize === 'md' || screenSize === 'lg' || screenSize === 'xl' || screenSize === '2xl') && (
        <div className='row'>
          <Select
            className='cell-3'
            label={translateFromEnglish('State', 'Estado', translationLanguage)}
            invalid={fieldErrors['Company State'] != null}
            options={stateOptions}
            setter={value => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyState: value })}
          />
          <TextField
            className='cell-2'
            label={translateFromEnglish('Zip Code', 'Código Postal', translationLanguage)}
            value={companySponsorshipInfo.companyZipCode}
            invalid={fieldErrors['Company Zip Code'] != null}
            onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyZipCode: evt.target.value })}
          />
        </div>
      )}
      {(screenSize === 'xs' || screenSize === 'sm') && (
        <>
          <div className='row'>
            <Select
              className='cell-3'
              label={translateFromEnglish('State', 'Estado', translationLanguage)}
              invalid={fieldErrors['Company State'] != null}
              options={stateOptions}
              setter={value => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyState: value })}
            />
          </div>
          <div className='row'>
            <TextField
              className='cell-2'
              label={translateFromEnglish('Zip Code', 'Código Postal', translationLanguage)}
              value={companySponsorshipInfo.companyZipCode}
              invalid={fieldErrors['Company Zip Code'] != null}
              onChange={evt => setCompanySponsorshipInfo({ ...companySponsorshipInfo, companyZipCode: evt.target.value })}
            />
          </div>
        </>
      )}
      <div className='row'>
        <Checkbox
          checked={companySponsorshipInfo.selfEmployed}
          invalid={fieldErrors['Self Employed'] != null}
          onChange={(evt) => setCompanySponsorshipInfo({
            ...companySponsorshipInfo,
            selfEmployed: evt.target.checked
          })}
          label={translateFromEnglish('Are you self employed?', '¿Trabajas por cuenta propia?', translationLanguage)}
        />
      </div>
      {selfEmploymentComponent}
    </>
  )

  const companyForm =
    <>
      <br />
      <br />
      {isWedIndustryStudent && sponsorshipComponent}
      {isWedIndustryStudent && (
        <div className='row'>
          <Checkbox
            checked={companySponsorshipInfo.employedByDifferentCompany}
            invalid={fieldErrors['Employed By Different Company'] != null}
            onChange={(evt) => setCompanySponsorshipInfo({
              ...companySponsorshipInfo,
              employedByDifferentCompany: evt.target.checked
            })}
            label={translateFromEnglish('Are you currently employed by a different organization?', '¿Está actualmente empleado por una organización diferente?', translationLanguage)}
          />
        </div>
      )}

      {isWedIndustryStudent && companySponsorshipInfo.employedByDifferentCompany ? companyComponent : ''}
      {!isWedIndustryStudent && companyComponent}
    </>

  return (
    <>
      {(isWedIndustryStudent || companySupported) && (
        <>
          <div className='page-header'>
            <div className='contents'>
              {translateFromEnglish('Enter Company Sponsorship Information', 'Ingrese la información de patrocinio de la empresa', translationLanguage)}
            </div>
          </div>

          <div className='page-body'>
            {companyForm}
          </div>
        </>)}
    </>
  )
}

// --functions
async function saveStudent (props, student, companySponsorshipInfo, isWedIndustryStudent, setGlobalError, setFieldErrors, isCartLink) {
  const token = await recaptcha('saveStudent')

  // TODO: Need to not add company name???
  if (!companySponsorshipInfo.companyName || !companySponsorshipInfo.companyAddress || !companySponsorshipInfo.companyCity || !companySponsorshipInfo.companyState || !companySponsorshipInfo.companyZipCode) {
    if (!companySponsorshipInfo.employedByDifferentCompany) {
      companySponsorshipInfo.companyName = companySponsorshipInfo.sponsoringCompanyName
      companySponsorshipInfo.companyAddress = companySponsorshipInfo.sponsoringCompanyAddress
      companySponsorshipInfo.companyCity = companySponsorshipInfo.sponsoringCompanyCity
      companySponsorshipInfo.companyState = companySponsorshipInfo.sponsoringCompanyState
      companySponsorshipInfo.companyZipCode = companySponsorshipInfo.sponsoringCompanyZipCode
    }
  }

  if (!student.address1 || !student.city || !student.state || !student.zipCode) {
    student.address1 = companySponsorshipInfo.sponsoringCompanyAddress
    student.address2 = null
    student.city = companySponsorshipInfo.sponsoringCompanyCity
    student.state = companySponsorshipInfo.sponsoringCompanyState
    student.zipCode = companySponsorshipInfo.sponsoringCompanyZipCode
  }

  const payload = preparePayload({ ...student, ...companySponsorshipInfo })

  Object.assign(payload, { recaptchaToken: token })

  try {
    const response = await http(setGlobalError, http => http.post('/api/SelfService/IndustryStudents', payload))

    student.publicId = response.data.publicId
    student.companySponsorshipInfo = companySponsorshipInfo

    props.dispatch({
      type: 'ADD_STUDENT',
      data: student
    })
    props.dispatch({
      type: 'DEFAULT_ADDRESS',
      data: student
    })
    if (student.contacts && student.contacts.length > 1) {
      props.dispatch({
        type: 'DEFAULT_CONTACT',
        data: student.contacts[0]
      })
    }
    setTimeout(() => {
      props.history.push('/students?isWedIndustry=true')
    }, 0)
  } catch (err) {
    if (err.response != null && err.response.status === 400) {
      const { data } = err.response
      setGlobalError('Please fix the errors on the page and try again.')
      if (data.validationErrors != null) {
        setFieldErrors(formatErrors(data, 'Contact'))
      } else {
        setFieldErrors({})
      }
    } else if (err.response != null && err.response.status === 422) {
      setGlobalError(err.response.data.message)
      setFieldErrors({})
    } else if (err.response != null && err.response.status === 424) {
      setGlobalError('It looks like you\'re going pretty fast! Please wait a moment and try again, or contact us at 405-717-4900 to enroll.')
      setFieldErrors({})
    } else {
      setFieldErrors({})
    }
  }
  window.scrollTo(0, 0)
}

// --mappings
export default connect(state => ({
  defaultAddress: state.defaultAddress,
  defaultContact: state.defaultContact
}))(AddStudentPage)
